import { toRedactor } from '@contentstack/json-rte-serializer/lib/toRedactor'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import keyBy from 'lodash/keyBy'
import map from 'lodash/map'

import {
  ExternalLink,
  Field,
  JsonField,
  LinkField,
  ObjectField,
  Page,
  ProductField,
  Reference,
  ReferenceField,
  RteField,
  Scalars,
} from '@Generated/graphql'

const isJSONField = (field: Field | undefined): field is JsonField => field?.__typename === 'JSONField'
const isObjectField = (field: Field | undefined): field is ObjectField => field?.__typename === 'ObjectField'
const isReferenceField = (field: Field | undefined): field is ReferenceField => field?.__typename === 'ReferenceField'
const isLinkField = (field: Field | undefined): field is LinkField => field?.__typename === 'LinkField'
const isProductField = (field: Field | undefined): field is ProductField => field?.__typename === 'ProductField'
const isExternalLinkReference = (reference: Reference | undefined): reference is ExternalLink => reference?.__typename === 'ExternalLink'
const isPageReference = (reference: Reference | undefined): reference is Page => reference?.__typename === 'Page'
const isRTEField = (field: Field | undefined): field is RteField => field?.__typename === 'RTEField'

export const getRTEFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isRTEField(field) ? field : null
}

const resolveRTEReference = <T extends Scalars['JSON']>(input: T, references: Record<string, Reference>): T => {
  if (!input) {
    return input
  }

  if (isArray(input)) {
    return map(input, item => resolveRTEReference(item, references)) as T
  }

  if (!isObject(input)) {
    return input
  }

  // We have to clone input as it is readonly on server side
  const result = { ...(input as any) }

  if (input.children) {
    result.children = resolveRTEReference(input.children, references)
  }

  if (!input.referenceKey) {
    return result
  }

  const reference = references[input.referenceKey as string]

  if (isExternalLinkReference(reference) || isPageReference(reference)) {
    // TODO: remove when graph-api doesn't return HTML for ExternalLink anymore
    if (includes(reference.url, '<')) {
      console.warn('url contains html characters', reference.url)
      return result
    }

    if (!result.attrs) {
      result.attrs = {}
    }

    // We have to clone attrs as they are readonly on server side
    result.attrs = {
      ...result.attrs,
      href: reference.url,
    }
  }

  return result
}

export const convertRTEToHTML = (data: RteField | null | undefined) => {
  if (!data || !data.value) {
    return ''
  }
  let result = data.value
  if (data.references && data.references.length !== 0) {
    const references = keyBy(data.references, 'referenceKey')
    result = resolveRTEReference(data.value, references)
  }
  return toRedactor(result)
}

export const getRTEFieldText = (fields: Field[], key: string) => {
  const data = getRTEFieldValue(fields, key)
  return convertRTEToHTML(data)
}

export const getJSONFieldValue = <T extends Scalars['JSON']>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isJSONField(field) ? field.value as T : null
}

export const getObjectFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isObjectField(field) ? (field.fields || []) : null
}

export const getReferenceFieldValue = <T extends Reference>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isReferenceField(field) ? (field.references || []) as T[] : null
}

export const getLinkFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isLinkField(field) ? field : null
}

export const getLinkType = (link: LinkField) => {
  const reference = first(link?.references)
  if (isPageReference(reference)) {
    return reference.type
  }
  return reference?.__typename || ''
}

export const getProductFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  if (isProductField(field) && field.product) {
    return field.product
  }
  return null
}

export const getProductReferenceValue = (fields: Field[], key: string) => {
  const referenceField = getReferenceFieldValue<Page>(fields, key)

  if (!referenceField) {
    return []
  }

  return compact(
    map(
      referenceField,
      x => getProductFieldValue(x.fields || [], 'product'),
    ),
  )
}

export const getPageReferences = (references: Reference[]): Page[] => filter(references, isPageReference)
